// LoadGarmentNode — the editor face of the `loadGarment` event node (ENG-1108).
// Picks one of the character's garment assets; the runtime writes it to
// garmentSlot, the same store the content browser's "Apply to character" writes
// to, so the node and the menu are two ways of saying one thing.
//
// Deliberately the same shape as LoadEnvironmentNode, down to the wording of
// its empty and dangling states: it is the same question asked of a different
// catalogue, and two nodes that do the same job should not look like two
// different features.
//
//   - The asset list comes live from CustomAssetManager + subscribe, so an
//     upload shows up without a reload.
//   - An empty catalogue renders a LABELED inert state naming the fix, not an
//     empty dropdown that reads as broken.
//   - Both the id AND the name are stored: the id is what the runtime resolves
//     (the stable server identity), the name is what the node can still display
//     once the row is gone, so a dangling reference reads as a named, flagged
//     selection instead of a blank select.

import { Handle, Position } from '@xyflow/react'
import { useState, useEffect } from 'react'
import { isFired } from './useFiredFlash'
import { CustomAssetManager } from '../../lib/CustomAssetManager.js'
import { garmentRowLabel, garmentRowId } from '../../lib/loadGarmentNode.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { selectedBorder } from './selectedBorder.js'

const HEADER_BG     = '#3a1020'
const HEADER_ACTIVE = '#5c1a33'
const ACCENT        = '#f2789f'   // the garment kind colour (assetKindColors.js)
const BORDER_SEL    = '#f2789f'
const BORDER_IDLE   = '#4a1628'

const garmentRows = () => [...CustomAssetManager.listGarmentAssets()]

export default function LoadGarmentNode({ data, selected }) {
  const fired = isFired(data)
  const stop  = e => e.stopPropagation()
  const [rows, setRows] = useState(garmentRows)

  useEffect(() => {
    setRows(garmentRows())
    return CustomAssetManager.subscribe(() => setRows(garmentRows()))
  }, [])

  const selectedId = data.garmentId ?? ''
  const known      = rows.some(r => garmentRowId(r) === selectedId)
  // Dangling: something is selected but the character no longer owns it. Said
  // on the node, where the author can see it before firing anything — the
  // runtime will refuse to wear it.
  const dangling   = !!selectedId && rows.length > 0 && !known

  const fieldStyle = {
    width: '100%', background: '#12060a', border: `1px solid ${dangling ? '#a04030' : '#45182a'}`,
    color: dangling ? '#e08060' : '#f0a0be', padding: '4px 6px', borderRadius: 3, fontSize: 10,
    fontFamily: 'var(--m-mono)', outline: 'none', boxSizing: 'border-box',
  }
  // Every in-node control opts out of the node drag, or dragging the control
  // drags the NODE instead (nodeControlClass.js). The select is `scrollable`:
  // a wheel over an open list must scroll it, not zoom the canvas.
  const CONTROL = nodeControlClass()
  const SELECT_CLASS = nodeControlClass({ scrollable: true })
  const checkRow = {
    display: 'flex', alignItems: 'center', gap: 6,
    color: '#b07088', fontSize: 9, marginTop: 5, cursor: 'pointer',
  }

  return (
    <div style={{
      background: selected ? '#26101a' : '#170a10',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(BORDER_IDLE) : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 240, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>👕</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Load Garment</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>

      <div className="nodrag" style={{ padding: '8px 12px' }} onPointerDown={stop} onMouseDown={stop}>
        {rows.length > 0 ? (
          <select
            className={SELECT_CLASS}
            data-testid="load-garment-select"
            value={selectedId}
            onChange={e => {
              const id = e.target.value
              const row = rows.find(r => garmentRowId(r) === id)
              data.onChange?.('garmentId', id)
              data.onChange?.('garmentName', row ? garmentRowLabel(row) : '')
            }}
            onMouseDown={stop}
            style={fieldStyle}
          >
            {/* "None" is a real setting: it is how a character boots undressed
                without deleting this node. Labelled as the choice it is. */}
            <option value="">{'— none (wear no garment) —'}</option>
            {dangling && (
              <option value={selectedId}>
                {data.garmentName || selectedId} (missing)
              </option>
            )}
            {rows.map(r => (
              <option key={garmentRowId(r)} value={garmentRowId(r)}>{garmentRowLabel(r)}</option>
            ))}
          </select>
        ) : (
          <div style={{ color: '#c08050', fontSize: 9, lineHeight: 1.5 }}>
            this character has no garments — drop a Marvelous .glb onto the
            content browser and import it as a garment
          </div>
        )}
        {dangling && (
          <div style={{ color: '#e08060', fontSize: 9, marginTop: 4 }}>
            selected garment is no longer on this character — it will not load
          </div>
        )}

        {/* The same knobs `?garment…` exposes, so a node and a deep link can
            express the same load. Defaults match the URL path exactly, so an
            untouched node behaves like a plain `?garment=`. */}
        {rows.length > 0 && (
          <div style={{ marginTop: 8, borderTop: '1px solid #2a0f18', paddingTop: 6 }}>
            <label style={checkRow}>
              <input type="checkbox" className={CONTROL} checked={data.hideOutfit !== false} onMouseDown={stop}
                onChange={e => data.onChange?.('hideOutfit', e.target.checked)} />
              <span>hide the rig&apos;s own outfit</span>
            </label>
            <label style={checkRow}>
              <input type="checkbox" className={CONTROL} checked={data.cullSkin !== false} onMouseDown={stop}
                onChange={e => data.onChange?.('cullSkin', e.target.checked)} />
              <span>cull body skin under the garment</span>
            </label>
            <label style={checkRow} title="A garment authored for another figure should LOOK wrong unless you ask for the fit.">
              <input type="checkbox" className={CONTROL} checked={data.applyFit === true} onMouseDown={stop}
                onChange={e => data.onChange?.('applyFit', e.target.checked)} />
              <span>apply the advisory fit transform</span>
            </label>
          </div>
        )}
      </div>

      {/* in-garment data pin (id or name; overrides the dropdown when wired) */}
      <div style={{ padding: '4px 12px 8px 20px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-garment"
          style={{ background: '#e07040', width: 10, height: 10, borderRadius: '50%', border: '2px solid #e0704044' }} />
        <span style={{ color: '#4a1e2c', fontSize: 9 }}>GARMENT (id or name — wired overrides dropdown)</span>
      </div>
    </div>
  )
}
